import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';
import { CheckCircle, MapPin, Globe, TrendingUp, Users, Award, ChevronDown, Loader2, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SponsorshipOpportunity = () => {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    company_type: '',
    budget_range: '',
    message: '',
    how_heard: ''
  });

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/sponsorships`, formData);
      if (response.data.success) {
        setIsSubmitted(true);
        toast.success('Inquiry submitted successfully!');
      }
    } catch (error) {
      toast.error('Failed to submit inquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: "Is this sponsorship exclusive?",
      answer: "Yes. We are offering one exclusive strategic partner."
    },
    {
      question: "Do you provide lead reporting?",
      answer: "Yes — we provide a summary of leads captured, event outcomes and deliverables."
    },
    {
      question: "Can we customise deliverables?",
      answer: "Yes — within the scope of the exhibition and agreed sponsor outcomes."
    },
    {
      question: "Can this include Dubai/GCC targeting?",
      answer: "Yes — Dubai is a major focus and a key reason this package is premium."
    }
  ];

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleCTAClick = () => {
    // Scroll to form section
    document.getElementById('enquiry-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <SEO page="sponsorship" />
      <Header onApplyClick={() => navigate('/#apply-form')} />
      
      {/* Hero Banner */}
      <div className="relative w-full h-[600px] overflow-hidden mt-20">
        <img
          src="https://customer-assets.emergentagent.com/job_franchise-phoenix/artifacts/6q1n8gwm_file_00000000fb2c71f4a1b9139f14eb3ac7.png"
          alt="Sponsor our Dubai London Franchise Exhibition"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Sponsor our Dubai + London Franchise Exhibition</h1>
          <p className="text-2xl mb-6">London + Dubai Exhibitions | £30,000 Strategic Sponsorship Partner</p>
          <Button
            onClick={handleCTAClick}
            size="lg"
            className="bg-accent-primary hover:bg-accent-primary/90 font-bold px-8 py-6 text-lg"
            style={{ color: 'white' }}
          >
            Enquire Now
          </Button>
        </div>
      </div>
      
      {/* Awards Carousel Section */}
      <div className="bg-gradient-to-r from-accent-primary to-accent-primary/80 py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-4xl font-bold text-white text-center mb-4">Award-Winning Excellence</h2>
          <p className="text-xl text-white text-center mb-12">Recognized for Outstanding Service & Innovation Since 2017</p>
          
          <div className="relative overflow-hidden">
            <div className="flex gap-6 overflow-x-auto pb-4 scrollbar-hide">
              {[
                { year: '2017', org: 'Salon Awards', awards: ['🏆 Best Hair Extensions Salon', '🏆 Best Customer Experience'] },
                { year: '2018', org: 'Salon Awards', awards: ['🏆 Best Designed Salon', '🏆 Best Customer Services', '🏆 Best Hygiene and Cleanliness', '🏆 Best Recommended Salon'] },
                { year: '2019', org: 'Industry Awards', awards: ['🥈 Finalist - Midlands Beauty Industry', '🥈 Finalist - Great British Hair & Beauty'] },
                { year: '2020', org: 'Salon Awards', awards: ['🏆 Winner - Best Hair Extension Salon', '🏆 Winner - Best Creative Image', '🥈 Finalist - Best Customer Experience', '🥈 Finalist - Best Salon West Midlands'] },
                { year: '2022', org: 'Multiple Awards', awards: ['🏆 Winner - Best Hair Extensions Salon', '🏆 Mobile & Emerging Technology StartUp'] },
                { year: '2024', org: 'National Recognition', awards: ['⭐ Fresha - Most Highly Recommended', '🏆 UK Hair & Beauty - Hairloss Specialist Winner'] },
                { year: '2025', org: 'HBA Awards', awards: ['🥈 Finalist - Hair Extensions Specialist', '🏅 Top 50 Salon UK', '⭐ Most Recommended Salon UK'] },
                { year: '2025', org: 'UK Hair & Beauty', awards: ['🏆 Winner - Hair Extensions & Hairloss Specialist', '🏆 Winner - Hairloss Replacement Specialist'] },
                { year: '2025', org: 'Salon Awards', awards: ['🏆 Winner - Best Hair Extensions Salon', '🏆 Winner - Best Hair Extensions Artist', '🥈 Finalist - Best Customer Experience', '🥈 Finalist - Creative Image'] },
                { year: '2025', org: 'Recognition', awards: ['🏆 Women Like Me Awards', '⭐ Fresha - Most Highly Recommended', '🏆 BTG Awards - Hair Extensions Specialist'] }
              ].map((award, index) => (
                <div key={index} className="flex-shrink-0 w-80 bg-white rounded-xl p-6 shadow-xl hover:shadow-2xl transition-shadow duration-300">
                  <div className="text-5xl font-bold text-accent-primary mb-2">{award.year}</div>
                  <div className="border-t-4 border-accent-primary pt-4">
                    <p className="text-lg font-bold text-dark mb-3">{award.org}</p>
                    {award.awards.map((awardItem, idx) => (
                      <p key={idx} className="text-gray-600 mb-1">{awardItem}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="text-white text-center mt-8 text-lg italic">Scroll to see all our achievements →</p>
        </div>
      </div>
      
      <div className="pt-8 pb-16">
        <div className="container mx-auto px-4 max-w-6xl">
          
          {/* Main Intro */}
          <div className="bg-white rounded-2xl shadow-xl p-12 mb-12">
            <h2 className="text-4xl font-bold text-dark mb-6">Sponsor the Need a Hair Makeover® Franchise Expansion</h2>
            <p className="text-xl text-subtle mb-6">
              One exclusive partner. Two global stages. Premium visibility in a booming niche sector.
            </p>
            <p className="text-lg text-subtle mb-6">
              We are inviting one strategic sponsor to partner with Need a Hair Makeover® as we exhibit in London and Dubai to accelerate our UK + GCC franchise expansion.
            </p>
            <p className="text-lg text-subtle mb-6">
              This is a board-ready, high-visibility sponsorship opportunity designed for organisations that want:
            </p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span className="text-lg">Premium brand exposure</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span className="text-lg">High-value investor and franchise leads</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span className="text-lg">Positioning in a luxury, high-growth hair & beauty niche</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span className="text-lg">A credible impact story (confidence, wellbeing, entrepreneurship)</span>
              </li>
            </ul>
            
            <div className="bg-accent-primary/10 rounded-xl p-6 mb-6">
              <p className="text-2xl font-bold text-dark mb-2">Investment: £30,000</p>
              <p className="text-xl text-dark">Availability: 1 exclusive sponsor only</p>
            </div>

            <Button
              onClick={handleCTAClick}
              size="lg"
              className="bg-accent-primary hover:bg-accent-primary/90 font-bold px-8 py-4 text-lg"
              style={{ color: 'white' }}
            >
              Request Sponsorship Pack
            </Button>
          </div>

          {/* Why This Works - with Video */}
          <div className="bg-white rounded-2xl shadow-xl p-12 mb-12">
            <div className="grid md:grid-cols-12 gap-8">
              {/* Video - Flush Left */}
              <div className="md:col-span-5">
                <div className="sticky top-24">
                  <h3 className="text-2xl font-bold text-dark mb-4">Watch: Sponsorship Introduction</h3>
                  <div className="rounded-xl overflow-hidden shadow-lg">
                    <video
                      src="https://customer-assets.emergentagent.com/job_franchise-phoenix/artifacts/324exx7s_Diane%20Shawe%20introducing%20sponsorshipopportunity%202026.mp4"
                      className="w-full h-auto"
                      controls
                      preload="metadata"
                      poster=""
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <p className="text-sm text-subtle mt-3 text-center italic">Diane Shawe - Introducing the Sponsorship Opportunity</p>
                </div>
              </div>

              {/* Content - Right Side */}
              <div className="md:col-span-7">
                <h2 className="text-3xl font-bold text-dark mb-6">Why This Opportunity Works</h2>
                <p className="text-lg text-subtle mb-4">
                  Need a Hair Makeover® operates in a specialist sector with long-term demand:
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center">
                    <Award className="w-5 h-5 text-accent-primary mr-3" />
                    <span className="text-lg">Hair extensions</span>
                  </li>
                  <li className="flex items-center">
                    <Award className="w-5 h-5 text-accent-primary mr-3" />
                    <span className="text-lg">Hair-loss solutions</span>
                  </li>
                  <li className="flex items-center">
                    <Award className="w-5 h-5 text-accent-primary mr-3" />
                    <span className="text-lg">Confidence restoration services</span>
                  </li>
                  <li className="flex items-center">
                    <Award className="w-5 h-5 text-accent-primary mr-3" />
                    <span className="text-lg">Premium consultations and specialist systems</span>
                  </li>
                </ul>
                <div className="bg-gray-50 rounded-lg p-6">
                  <p className="text-xl font-bold text-dark mb-2">This is not "another salon brand."</p>
                  <p className="text-lg text-subtle">
                    This is a specialist model with strong emotional value and commercial strength — making sponsorship especially powerful.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* What You Receive */}
          <div className="bg-white rounded-2xl shadow-xl p-12 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-8">What You Receive as Our Strategic Sponsor</h2>
            
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-dark mb-4 flex items-center">
                  <span className="bg-accent-primary text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">1</span>
                  Headline Partner Branding
                </h3>
                <p className="text-lg text-subtle mb-3">Be featured as the exclusive sponsor across:</p>
                <ul className="space-y-2 ml-13">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Exhibition stand backdrops (London + Dubai)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Printed materials and brochures</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Investor and franchise promo packs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>On-site signage and digital screens (where available)</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-dark mb-4 flex items-center">
                  <span className="bg-accent-primary text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">2</span>
                  Co-Branded Lead Capture & Visibility
                </h3>
                <ul className="space-y-2 ml-13">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Sponsor logo integrated into franchise enquiry capture journey</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Featured placement on our brochure landing page and sponsor page</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>"Recommended Partner" positioning to franchisees</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-dark mb-4 flex items-center">
                  <span className="bg-accent-primary text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">3</span>
                  High-Value Lead Access
                </h3>
                <p className="text-lg text-subtle mb-3">Receive curated warm leads from:</p>
                <ul className="space-y-2 ml-13">
                  <li className="flex items-start">
                    <Users className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Serious franchise investors</span>
                  </li>
                  <li className="flex items-start">
                    <Users className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Entrepreneurs looking to launch in UK/GCC</span>
                  </li>
                  <li className="flex items-start">
                    <Users className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Salon owners seeking specialist niche diversification</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-dark mb-4 flex items-center">
                  <span className="bg-accent-primary text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">4</span>
                  Speaking & Thought Leadership
                </h3>
                <ul className="space-y-2 ml-13">
                  <li className="flex items-start">
                    <TrendingUp className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>One sponsor speaking slot / featured segment at London exhibition campaign</span>
                  </li>
                  <li className="flex items-start">
                    <TrendingUp className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>One sponsor speaking slot / featured segment at Dubai exhibition campaign</span>
                  </li>
                  <li className="text-sm text-subtle ml-7">(Format dependent on event organiser availability.)</li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-dark mb-4 flex items-center">
                  <span className="bg-accent-primary text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">5</span>
                  Premium Content & PR Rollout
                </h3>
                <ul className="space-y-2 ml-13">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Sponsor mentioned in press releases</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Sponsor featured in short-form content (Reels/Shorts/TikToks)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-accent-primary mr-2 flex-shrink-0 mt-1" />
                    <span>Co-branded media clips captured at the exhibition stand</span>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-2xl font-bold text-dark mb-4 flex items-center">
                  <span className="bg-accent-primary text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">6</span>
                  Long-Term Network Value
                </h3>
                <p className="text-lg text-subtle ml-13">
                  You will be positioned as a preferred partner as the franchise scales — giving you future commercial access to a growing network.
                </p>
              </div>
            </div>
          </div>

          {/* Who This Is For */}
          <div className="bg-white rounded-2xl shadow-xl p-12 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-6">Who This Sponsorship Is Perfect For</h2>
            <p className="text-lg text-subtle mb-6">
              We are specifically seeking a sponsor aligned with business growth, beauty/wellness, or premium consumer services, including:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span>Business banking / payment processing / merchant accounts</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span>POS systems and salon software</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span>Insurance providers (business / salon / equipment)</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span>Accountancy, legal, and franchise compliance firms</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span>Commercial property and fit-out companies</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span>Beauty/hair product manufacturers and suppliers</span>
              </div>
              <div className="flex items-start">
                <CheckCircle className="w-5 h-5 text-accent-primary mr-3 flex-shrink-0 mt-1" />
                <span>Wellness or hair-growth tech providers</span>
              </div>
            </div>
            <div className="bg-accent-primary/10 rounded-lg p-6 mt-6">
              <p className="text-lg text-dark">
                If your customers include entrepreneurs, SME owners, franchise seekers, or premium beauty/wellness clients — this is a strong match.
              </p>
            </div>
          </div>

          {/* Why Sponsor Us */}
          <div className="bg-gradient-to-r from-accent-primary to-accent-primary/80 text-white rounded-2xl shadow-xl p-12 mb-12">
            <h2 className="text-3xl font-bold mb-6">Why Sponsor Us Instead of a Generic Event?</h2>
            <p className="text-xl mb-6">
              Because this partnership positions you inside a high-value niche with:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                <span className="text-lg">Premium service pricing</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                <span className="text-lg">Repeat customer demand</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                <span className="text-lg">Strong emotional outcomes (confidence restoration)</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="w-6 h-6 mr-3 flex-shrink-0 mt-1" />
                <span className="text-lg">UK + GCC expansion narrative (global growth story)</span>
              </li>
            </ul>
            <div className="bg-white/20 rounded-lg p-6">
              <p className="text-xl font-bold mb-2">Sponsors don't want "logos on banners."</p>
              <p className="text-lg">Sponsors want relevance, leads, and positioning.</p>
              <p className="text-lg font-bold mt-2">That's what this delivers.</p>
            </div>
          </div>

          {/* Package Summary */}
          <div className="bg-white rounded-2xl shadow-xl p-12 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-8 text-center">Sponsorship Summary</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-sm text-subtle mb-1">Package</p>
                <p className="text-2xl font-bold text-dark">Platinum Strategic Sponsor</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-sm text-subtle mb-1">Investment</p>
                <p className="text-2xl font-bold text-accent-primary">£30,000</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-sm text-subtle mb-1">Coverage</p>
                <p className="text-2xl font-bold text-dark">London + Dubai Exhibitions</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-6">
                <p className="text-sm text-subtle mb-1">Exclusivity</p>
                <p className="text-2xl font-bold text-dark">1 sponsor only</p>
              </div>
            </div>
            <div className="bg-accent-primary/10 rounded-lg p-6 mt-6">
              <p className="text-lg font-semibold text-dark">Ideal Sponsor:</p>
              <p className="text-lg text-dark">Growth-focused organisation wanting premium visibility + lead flow</p>
            </div>
          </div>

          {/* Enquiry Form Section */}
          <div id="enquiry-form" className="bg-white rounded-2xl shadow-xl p-12 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-4 text-center">Request the Sponsorship Prospectus + Partner Call</h2>
            <p className="text-lg text-subtle mb-8 text-center">
              Complete the form below and our team will respond within 24–48 hours.
            </p>
            
            {isSubmitted ? (
              <div className="bg-accent-primary/10 rounded-xl p-8 text-center">
                <CheckCircle className="w-16 h-16 text-accent-primary mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-dark mb-2">Thank You!</h3>
                <p className="text-lg text-subtle">
                  Your inquiry has been received. Our team will contact you within 24–48 hours with the sponsorship prospectus and to schedule a partner call.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-dark mb-2">Company Name *</label>
                    <Input
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      placeholder="Your company name"
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark mb-2">Contact Name *</label>
                    <Input
                      name="contact_name"
                      value={formData.contact_name}
                      onChange={handleInputChange}
                      placeholder="Your full name"
                      required
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-dark mb-2">Email Address *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="you@company.com"
                      required
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-dark mb-2">Phone Number</label>
                    <Input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+44 ..."
                      className="w-full"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">Company Type *</label>
                  <select
                    name="company_type"
                    value={formData.company_type}
                    onChange={handleInputChange}
                    required
                    className="w-full h-10 px-3 py-2 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-accent-primary"
                  >
                    <option value="">Select your industry...</option>
                    <option value="Franchise finance provider">Franchise Finance Provider</option>
                    <option value="POS systems and salon software">POS Systems / Salon Software</option>
                    <option value="Insurance provider">Insurance Provider</option>
                    <option value="Accountancy or legal firm">Accountancy / Legal Firm</option>
                    <option value="Commercial property">Commercial Property / Fit-out</option>
                    <option value="Beauty product manufacturer">Beauty / Hair Product Manufacturer</option>
                    <option value="Wellness or hair-growth tech">Wellness / Hair-Growth Tech</option>
                    <option value="Marketing or media agency">Marketing / Media Agency</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">Budget Range</label>
                  <select
                    name="budget_range"
                    value={formData.budget_range}
                    onChange={handleInputChange}
                    className="w-full h-10 px-3 py-2 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-accent-primary"
                  >
                    <option value="">Select budget range...</option>
                    <option value="Full package (£30,000)">Full Package (£30,000)</option>
                    <option value="Open to discussion">Open to Discussion</option>
                    <option value="Need more information first">Need More Information First</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">Message</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your interest in sponsoring, any specific questions, or what outcomes you're looking for..."
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-200 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-accent-primary"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-dark mb-2">How did you hear about this opportunity?</label>
                  <Input
                    name="how_heard"
                    value={formData.how_heard}
                    onChange={handleInputChange}
                    placeholder="e.g., LinkedIn, Website, Referral..."
                    className="w-full"
                  />
                </div>
                
                <div className="text-center pt-4">
                  <Button
                    type="submit"
                    size="lg"
                    className="bg-accent-primary hover:bg-accent-primary/90 px-12 py-6 text-lg font-semibold"
                    style={{ color: 'white' }}
                    disabled={isSubmitting}
                    data-testid="sponsorship-form-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5 mr-2" />
                        Request Sponsorship Prospectus
                      </>
                    )}
                  </Button>
                  <p className="text-sm text-subtle mt-4">
                    By submitting, you agree to be contacted about sponsorship opportunities.
                  </p>
                </div>
              </form>
            )}
          </div>

          {/* FAQs */}
          <div className="bg-white rounded-2xl shadow-xl p-12 mb-12">
            <h2 className="text-3xl font-bold text-dark mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  className="border border-gray-200 rounded-lg overflow-hidden transition-all duration-200 hover:border-accent-primary"
                >
                  <button
                    onClick={() => toggleFAQ(index)}
                    className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors duration-200"
                  >
                    <h3 className="text-lg font-semibold text-dark pr-4">{faq.question}</h3>
                    <ChevronDown 
                      className={`w-5 h-5 text-accent-primary flex-shrink-0 transition-transform duration-200 ${
                        openFAQ === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div 
                    className={`transition-all duration-300 ease-in-out ${
                      openFAQ === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    } overflow-hidden`}
                  >
                    <div className="px-5 pb-5 pt-2 bg-gray-50">
                      <p className="text-subtle leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="bg-gradient-to-r from-accent-primary to-accent-primary/80 text-white rounded-2xl shadow-xl p-12 text-center">
            <h2 className="text-4xl font-bold mb-6">Become the Sponsor Behind the Next UK + GCC Expansion Story</h2>
            <p className="text-xl mb-8">
              If you want premium visibility, high-value leads, and alignment with a niche beauty franchise with purpose — enquire today.
            </p>
            <Button
              onClick={handleCTAClick}
              size="lg"
              className="bg-white text-accent-primary hover:bg-gray-100 px-12 py-6 text-xl font-semibold shadow-lg"
            >
              Enquire Now
            </Button>
          </div>

        </div>
      </div>
      
      <Footer />
    </div>
  );
};
